import { useCallback } from 'react';

import { useOptionalRouter } from './useOptionalRouter';

export type NavigateParams = {
    path: string;
    /** Carried to the destination as the router's location state, whatever shape it declares. */
    state?: unknown;
    /** Replaces the current entry instead of adding one, for a guard that sends a dead URL away. */
    replace?: boolean;
};

/**
 * Offers the navigation to the host shell, and says whether it took it. The host's router cancels
 * the event once it has navigated; nothing cancels it when the module runs on its own.
 */
const hostTookNavigation = (params: NavigateParams): boolean =>
    !window.dispatchEvent(new CustomEvent('mfe-navigate', { cancelable: true, detail: params }));

/**
 * The single way to navigate from inside the module.
 *
 * The host shell and the module each own a `history`, and neither hears the other's pushes: moving
 * the module's one changes the address bar while the host keeps lighting up the menu entry of the
 * page we just left, and keeps re-running its permission guard on the path we left. So every
 * navigation is offered to the host first, and only pushed locally when no host is listening --
 * standalone `yarn dev`, Storybook, tests -- or when the module is mounted without a router at all.
 *
 * Going back needs none of this: `history.goBack()` fires a real `popstate`, which both routers hear.
 */
export const useNavigate = () => {
    // The performance profile embedded in the host's colleague page is mounted without a Router; it
    // navigates through the host like everything else.
    const history = useOptionalRouter();

    // Stable across renders, so callers can hold it in a `useCallback` or an effect dependency.
    return useCallback(
        ({ path, state, replace }: NavigateParams) => {
            if (hostTookNavigation({ path, state, replace })) {
                return;
            }

            if (!history) {
                return;
            }

            // A caller that carries no state leaves the router call as it has always been written
            // here, `push(path)`, rather than handing it an explicit `undefined`.
            const move = replace ? history.replace : history.push;

            if (state === undefined) {
                move(path);
                return;
            }

            move(path, state);
        },
        [history],
    );
};
